/**
 * Sugestão de abreviação quando um campo estoura a largura tipográfica.
 *
 * Fonte: outras cartas do mesmo lote (e tags já salvas no modelo, se
 * disponíveis) com a mesma chave canônica — a forma curta mais frequente
 * que ainda cabe no limite medido com fontkit.
 */
import { FIELD_SPECS, getFonts, stringWidth } from "./limitString";

const normalizeKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const infosCompose = (card) =>
  `| ${card?.ano || ""} | ${card?.idioma || ""} | ${card?.num || ""}`;

const fieldValue = (card, fieldKey) => {
  if (fieldKey === "nome") return String(card?.nome ?? "");
  if (fieldKey === "edicao") return String(card?.edition ?? "");
  if (fieldKey === "infos") return infosCompose(card);
  return "";
};

/** Chave canônica: preferir o nome completo do modelo; senão o valor atual. */
const canonicalKey = (card, fieldKey, serialByNumber) => {
  const serial = serialByNumber?.[card?.serial];
  const model = serial?.gradableModel;
  if (fieldKey === "nome") {
    return normalizeKey(model?.name || card?.nome);
  }
  if (fieldKey === "edicao") {
    return normalizeKey(model?.edition || card?.edition);
  }
  if (fieldKey === "infos") {
    const ano = model?.year ?? card?.ano;
    const idioma = model?.language ?? card?.idioma;
    const num = model?.numbering ?? card?.num;
    return normalizeKey(`| ${ano || ""} | ${idioma || ""} | ${num || ""}`);
  }
  return normalizeKey(fieldValue(card, fieldKey));
};

const storedTagValue = (serial, fieldKey) => {
  const tag = serial?.gradableModel?.tag;
  if (!tag) return null;
  if (fieldKey === "nome") return tag.name ?? null;
  if (fieldKey === "edicao") return tag.edition ?? null;
  if (fieldKey === "infos") {
    if (tag.year == null && tag.language == null && tag.numbering == null) return null;
    return `| ${tag.year || ""} | ${tag.language || ""} | ${tag.numbering || ""}`;
  }
  return null;
};

/**
 * Conta formas curtas usadas por pares com a mesma chave canônica.
 * @returns {{ suggestion: string|null, count: number, candidates: Array<{value:string,count:number}> }}
 */
const suggestAbbreviation = ({
  fieldKey,
  card,
  peers = [],
  serialByNumber = {},
  fonts = getFonts(),
}) => {
  const spec = FIELD_SPECS.find((s) => s.key === fieldKey);
  if (!spec || !fonts) {
    return { suggestion: null, count: 0, candidates: [] };
  }

  const key = canonicalKey(card, fieldKey, serialByNumber);
  if (!key) return { suggestion: null, count: 0, candidates: [] };

  const current = fieldValue(card, fieldKey);
  const currentWidth = stringWidth(fonts[spec.fontKey], current, spec.size);
  if (currentWidth <= spec.limit) {
    return { suggestion: null, count: 0, candidates: [] };
  }

  const tallies = new Map();
  const bump = (raw) => {
    const value = String(raw ?? "").trim();
    if (!value || normalizeKey(value) === key) return;
    const width = stringWidth(fonts[spec.fontKey], value, spec.size);
    if (width > spec.limit) return;
    // Só conta se for realmente mais curto que o atual.
    if (value.length >= current.length && width >= currentWidth) return;
    tallies.set(value, (tallies.get(value) || 0) + 1);
  };

  for (const peer of peers) {
    if (!peer || peer.serial === card.serial) continue;
    if (canonicalKey(peer, fieldKey, serialByNumber) !== key) continue;
    bump(fieldValue(peer, fieldKey));
    const stored = storedTagValue(serialByNumber[peer.serial], fieldKey);
    if (stored) bump(stored);
  }

  const ownStored = storedTagValue(serialByNumber[card.serial], fieldKey);
  if (ownStored) bump(ownStored);

  const candidates = [...tallies.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.length - b.value.length);

  if (!candidates.length) return { suggestion: null, count: 0, candidates: [] };
  return {
    suggestion: candidates[0].value,
    count: candidates[0].count,
    candidates,
  };
};

/**
 * Patch para aplicar a sugestão de infos (`| ano | idioma | num`).
 */
const parseInfosSuggestion = (text) => {
  const m = String(text ?? "").match(/^\|\s*(.*?)\s*\|\s*(.*?)\s*\|\s*(.*?)\s*$/);
  if (!m) return null;
  return { ano: m[1], idioma: m[2], num: m[3] };
};

export {
  normalizeKey,
  suggestAbbreviation,
  parseInfosSuggestion,
  fieldValue,
  canonicalKey,
};
